/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommentMultiple16Regular } from '@fluentui/react-icons';
import { ActionButton, BaseButton, Button, IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Separator } from '@fluentui/react/lib/Separator';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DragAndDropList } from 'src/core/components/DragAndDropList';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

type QnaMakerQnaPairAlternateQuestionsSectionProps = {
    questions: string[];
    handleEditAlternateQuestions: (buttonPlace: string, section: string, action: string) => void;
    handleSubmitAlternateQuestions: (newData: Partial<QnaMakerQnaSwagger>, qnaId?: number) => void;
};

const messages = defineMessages({
    alternateQuestions: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.alternateQuestions',
        defaultMessage: 'Alternate questions'
    },
    description: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.description',
        defaultMessage:
            'Add an alternate question when there are multiple ways the same question can be asked. Alternate questions should be as semanitcally dissimilar as possible and should not exceed 10 alternate questions.'
    },
    addAlternateQuestion: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.addAlternateQuestion',
        defaultMessage: 'Add alternate question'
    },
    default: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.default',
        defaultMessage: 'Default'
    },
    textPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.textPlaceholder',
        defaultMessage: 'Add question and press enter'
    },
    pin: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.pin',
        defaultMessage: 'Pin'
    },
    edit: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.edit',
        defaultMessage: 'Edit'
    },
    delete: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.delete',
        defaultMessage: 'Delete'
    },
    iconAriaLAbel: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.iconAriaLAbel',
        defaultMessage: 'drag button { idx } press spacebar to select and use up/down arrow key to change the position'
    },
    collapse: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.collapse',
        defaultMessage: 'collapse Alternate Answer button'
    },
    expand: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.expand',
        defaultMessage: 'expand Alternate Answer button'
    }
});

const theme = getScopedTheme('QnaMakerQnaPairDetails');

const addButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    label: { color: theme.buttonColor },
    labelDisabled: { color: theme.disabledLabelColor }
});

export const QnaMakerQnaPairAlternateQuestionsSection = observer((props: QnaMakerQnaPairAlternateQuestionsSectionProps) => {
    const { questions = [], handleSubmitAlternateQuestions, handleEditAlternateQuestions } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { increaseNumberOfClicks } = useQnaMetricActions();

    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [areAlternateQuestionsExpanded, setAreAlternateQuestionsExpanded] = React.useState(false);
    const [isInDragMode, setIsInDragMode] = React.useState(false);

    const [alternateQuestionIdxInEditMode, setAlternateQuestionIdxInEditMode] = React.useState(null);

    const [isInAddMode, setIsInAddMode] = React.useState(false);
    const [editableNewAlternateQuestion, setEditableNewAlternateQuestion] = React.useState('');
    const [isDefaultQuestionInEditMode, setIsDefaultQuestionInEditMode] = React.useState(false);
    const [editableDefaultQuestion, setEditableDefaultQuestion] = React.useState(questions[0]);

    React.useEffect(() => setEditableDefaultQuestion(questions[0]), [questions[0]]);

    const reorderItem = (
        item: string,
        e: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button>
    ): void => {
        const newItems = [...questions];

        if (e.key === LuisKeyCodes.Space) {
            setIsInDragMode(!isInDragMode);
        } else if (isInDragMode && e.key === LuisKeyCodes.ArrowUp && questions.indexOf(item) !== 1) {
            e.preventDefault();
            const idx = questions.indexOf(item);
            newItems[idx] = questions[idx - 1];
            newItems[idx - 1] = item;
            (document.querySelector(`#aqbutton${idx - 1}`) as HTMLElement)?.focus();
        } else if (isInDragMode && e.key === LuisKeyCodes.ArrowDown && questions.indexOf(item) !== questions.length - 1) {
            e.preventDefault();
            const idx = questions.indexOf(item);
            newItems[idx] = questions[idx + 1];
            newItems[idx + 1] = item;
            (document.querySelector(`#aqbutton${idx + 1}`) as HTMLElement)?.focus();
        }

        handleSubmitAlternateQuestions({
            questions: newItems
        });
    };

    const onAlternateQuestionEdit = (idx: number, section: string) => {
        if (canWrite) {
            setAlternateQuestionIdxInEditMode(idx);
            handleEditAlternateQuestions('AlternateQuestions', section, 'Edit');
        }
    };

    const AlternateQuestionColumn = (prop: { question: string; idx: number }) => {
        const { question, idx } = prop;

        const [editableQuestion, setEditableQuestion] = React.useState(question);

        const updateAlternateQuestion = () => {
            setAlternateQuestionIdxInEditMode(null);
            const newQuestions = [...questions];
            newQuestions[idx] = editableQuestion;
            handleSubmitAlternateQuestions({ questions: newQuestions });
        };

        const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
            if (e.key === LuisKeyCodes.Enter) {
                updateAlternateQuestion();
            }

            if (e.key === LuisKeyCodes.Escape) {
                setEditableQuestion(questions[idx]);
                setAlternateQuestionIdxInEditMode(null);
            }
        };

        return (
            <>
                {alternateQuestionIdxInEditMode === idx ? (
                    <LuisTextField
                        required
                        autoFocus
                        onKeyUp={handleOnKeyUp}
                        dir={languageDirection}
                        value={editableQuestion}
                        onBlur={updateAlternateQuestion}
                        onChange={(_, v) => setEditableQuestion(v)}
                        placeholder={localize(messages.textPlaceholder)}
                        styles={{ root: { marginRight: '5px' } }}
                    />
                ) : (
                    <Stack onClick={() => onAlternateQuestionEdit(idx, 'ClickToEdit')} styles={{ root: { width: '100%' } }}>
                        <TruncatedText
                            maxWidth={'95%'}
                            styles={{ root: { cursor: 'text', color: theme.textColor, paddingTop: '5px', marginRight: '5px' } }}>
                            {question}
                        </TruncatedText>
                    </Stack>
                )}
            </>
        );
    };

    const pinQuestion = (idx: number) => {
        const newQuestions = [...questions];
        const oldDefaultQuestion = newQuestions[0];
        newQuestions[0] = newQuestions[idx];
        newQuestions[idx] = oldDefaultQuestion;
        handleSubmitAlternateQuestions({ questions: newQuestions });
    };

    const columns: IColumn[] = [
        {
            key: 'drag',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: (item, idx) => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="dragAlternateButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            id={`aqbutton${idx + 1}`}
                            ariaLabel={localize(messages.iconAriaLAbel, { idx: idx + 1 })}
                            iconProps={{
                                iconName: 'DragObject',
                                styles: { root: { cursor: 'move', color: isInDragMode ? 'grey' : null } }
                            }}
                            onKeyDown={e => reorderItem(item, e)}
                        />
                    }
                />
            )
        },
        {
            key: 'items',
            minWidth: 200,
            maxWidth: 200,
            name: '',
            onRender: (item, idx) => <AlternateQuestionColumn question={item} idx={idx + 1} />
        },
        {
            key: 'pin',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: (_, idx) => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="pinAlternateQuestionButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            disabled={!canWrite}
                            title={localize(messages.pin)}
                            ariaLabel={localize(messages.pin)}
                            iconProps={{
                                iconName: 'Pinned'
                            }}
                            onClick={() => {
                                increaseNumberOfClicks();
                                pinQuestion(idx + 1);
                            }}
                        />
                    }
                />
            )
        },
        {
            key: 'edit',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: (_, idx) => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editAlternateQuestionButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            title={localize(messages.edit)}
                            ariaLabel={localize(messages.edit)}
                            disabled={!canWrite || alternateQuestionIdxInEditMode === idx + 1}
                            iconProps={{
                                iconName: 'Edit',
                                styles: { root: { cursor: 'pointer' } }
                            }}
                            onClick={() => {
                                increaseNumberOfClicks();
                                onAlternateQuestionEdit(idx + 1, 'Button');
                            }}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            minWidth: 20,
            maxWidth: 20,
            name: '',
            onRender: item => (
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteAlternateQuestionButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            disabled={!canWrite}
                            title={localize(messages.delete)}
                            ariaLabel={localize(messages.delete)}
                            iconProps={{
                                iconName: 'Delete',
                                styles: { root: { cursor: 'pointer' } }
                            }}
                            onClick={() => {
                                increaseNumberOfClicks();
                                handleSubmitAlternateQuestions({
                                    questions: questions.filter((_, idx) => questions.indexOf(item) !== idx)
                                });
                                handleEditAlternateQuestions('AlternateQuestions', 'Button', 'Delete');
                            }}
                        />
                    }
                />
            )
        }
    ];

    const updateDefaultQuestion = () => {
        setIsDefaultQuestionInEditMode(false);
        const newQuestions = [...questions];
        newQuestions[0] = editableDefaultQuestion;
        handleSubmitAlternateQuestions({ questions: newQuestions });
    };

    const handleOnEditQuestion = () => {
        setIsInAddMode(true);
        increaseNumberOfClicks();
        handleEditAlternateQuestions('AlternateQuestions', 'Button', 'Add');
    };

    const handleOnDefaultKeyUp = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.Enter) {
            updateDefaultQuestion();
        }

        if (e.key === LuisKeyCodes.Escape) {
            setEditableDefaultQuestion(questions[0]);
            setIsDefaultQuestionInEditMode(false);
        }
    };

    const onSubmitNewAlternateQuestion = () => {
        if (editableNewAlternateQuestion.length > 0) {
            const newQuestions = [...questions];
            newQuestions.splice(1, 0, editableNewAlternateQuestion);
            handleSubmitAlternateQuestions({ questions: newQuestions });
        }
        setIsInAddMode(false);
        setEditableNewAlternateQuestion('');
    };

    const handleAddNewAlternateQuestion = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.Enter) {
            e.preventDefault();
            onSubmitNewAlternateQuestion();
        }
        if (e.key === LuisKeyCodes.Escape) {
            setIsInAddMode(false);
            setEditableNewAlternateQuestion('');
        }
    };

    return (
        <Stack role="button" tokens={{ childrenGap: 10 }}>
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <FontIcon
                    tabIndex={0}
                    aria-hidden={false}
                    onClick={() => setAreAlternateQuestionsExpanded(!areAlternateQuestionsExpanded)}
                    onKeyDown={e => {
                        if (e.key === LuisKeyCodes.Enter) {
                            setAreAlternateQuestionsExpanded(!areAlternateQuestionsExpanded);
                        }
                    }}
                    iconName={areAlternateQuestionsExpanded ? 'ChevronDown' : 'ChevronRight'}
                    style={{ cursor: 'pointer' }}
                    aria-label={areAlternateQuestionsExpanded ? localize(messages.collapse) : localize(messages.expand)}
                />
                <CommentMultiple16Regular />
                <LuisText fontWeight={600}>{localize(messages.alternateQuestions)}</LuisText>
                <LuisText fontWeight={600}>({questions.length})</LuisText>
            </Stack>
            {areAlternateQuestionsExpanded && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText variant="smallPlus" color={theme.descriptionColor}>
                        {localize(messages.description)}
                    </LuisText>
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addAlternateQuestionButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <ActionButton
                                disabled={!canWrite || isInAddMode}
                                styles={addButtonStyles}
                                onClick={handleOnEditQuestion}
                                iconProps={{ iconName: 'Add' }}
                                text={localize(messages.addAlternateQuestion)}
                            />
                        }
                    />
                    <Stack horizontal tokens={{ childrenGap: 15, padding: '0 0 0 5px' }}>
                        <FontIcon iconName="PinnedSolid" style={{ marginTop: '4px', fontSize: '12px' }} />
                        {isDefaultQuestionInEditMode ? (
                            <LuisTextField
                                required
                                autoFocus
                                onKeyUp={handleOnDefaultKeyUp}
                                dir={languageDirection}
                                value={editableDefaultQuestion}
                                onBlur={updateDefaultQuestion}
                                onChange={(_, v) => setEditableDefaultQuestion(v)}
                                styles={{ root: { width: '300px' } }}
                                placeholder={localize(messages.textPlaceholder)}
                            />
                        ) : (
                            <Stack onClick={() => setIsDefaultQuestionInEditMode(true)}>
                                <LuisText>{questions[0]}</LuisText>
                            </Stack>
                        )}
                    </Stack>
                    {questions.length > 1 && <Separator />}
                    {isInAddMode && (
                        <LuisTextField
                            autoFocus
                            dir={languageDirection}
                            value={editableNewAlternateQuestion}
                            styles={{ root: { width: '370px' } }}
                            onBlur={onSubmitNewAlternateQuestion}
                            placeholder={localize(messages.textPlaceholder)}
                            onChange={(_, v) => setEditableNewAlternateQuestion(v)}
                            onKeyUp={handleAddNewAlternateQuestion}
                        />
                    )}
                    <DragAndDropList<String>
                        items={[...questions.slice(1)]}
                        listColumns={columns}
                        onRenderItem={undefined}
                        canDragAndDrop={canWrite}
                        onEditItems={subQuestions =>
                            handleSubmitAlternateQuestions({
                                questions: [String(questions[0]), ...subQuestions.map(q => String(q))]
                            })
                        }
                    />
                </Stack>
            )}
        </Stack>
    );
});
